<template>
    <view-container>
        <template #header>
            <view-header back-button @back="emit(EmitEvents.BACK)" />
        </template>

        <form v-if="!!maximum && !!minimum" id="setup-plan" @submit.prevent="submit">
            <h1>{{ t('widget.dollar-cost-average.setup-plan.title') }}</h1>
            <p class="text center">{{ t('widget.dollar-cost-average.setup-plan.description') }}</p>

            <crypto-select
                quote-type="f2c"
                :label="t('widget.dollar-cost-average.setup-plan.crypto-select')"
                :modal-title="t('widget.dollar-cost-average.setup-plan.crypto-select.modal.title')"
                @updated="updateField"
            />

            <fiat-amount
                :label="t('widget.dollar-cost-average.fiat-amount.label')"
                :crypto-currency="currenciesStore.selectedCryptoCurrency.code"
                :maximum="maximum"
                :minimum="minimum"
                :value="dcaRequest.fiatAmount"
                @created="updateField"
                @updated="updateField"
            />

            <recurrence-select @created="updateField" @updated="updateField" />

            <wallet-address-select quote-type="f2c" :translation-domain="translationDomain" @created="updateField" @updated="updateField" />
        </form>

        <template #footer>
            <call-to-action form="setup-plan" :disabled="dcaRequest.communication === ApiCommunicationStates.COMMUNICATING">
                {{ t('widget.dollar-cost-average.setup-plan.submit') }}
            </call-to-action>
        </template>
    </view-container>
</template>

<script lang="ts" setup>
import CryptoSelect from '@/dollar-cost-average/components/crypto-select.vue';
import FiatAmount from '@/dollar-cost-average/components/fiat-amount.vue';
import RecurrenceSelect from '@/dollar-cost-average/components/recurrence-select.vue';
import { useDca } from '@/dollar-cost-average/composables/dca';
import { translationDomain } from '@/dollar-cost-average/types';
import { CallToAction, ViewContainer, ViewHeader } from '@btcdirect/components';
import WalletAddressSelect from '@btcdirect/components/form-fields/wallet-address-select.vue'; // TODO: Figure out how to use '@btcdirect/stores' without circular dependencies
import { useTranslations } from '@btcdirect/composables';
import { needsVerification } from '@btcdirect/functions';
import { EmitEvents, ValidatedFormFields } from '@btcdirect/shared/src/components/types';
import { ApiCommunicationStates } from '@btcdirect/shared/src/composables/api/types';
import { useClientInfoStore } from '@btcdirect/stores';
import { useCurrenciesStore } from '@btcdirect/stores/src/currencies'; // TODO: Figure out how to use '@btcdirect/stores' without circular dependencies
import { usePaymentMethodsStore } from '@btcdirect/stores/src/payment-methods'; // TODO: Figure out how to use '@btcdirect/stores' without circular dependencies
import { useWalletAddressesStore } from '@btcdirect/stores/src/wallets'; // TODO: Figure out how to use '@btcdirect/stores' without circular dependencies
import { SubmittedSymbol, ValidatedFieldData } from 'out-of-the-vue';
import { Ref, computed, provide, reactive, ref } from 'vue';

const emit = defineEmits<{ (event: 'completed' | 'failed' | 'back' | 'verify'): void }>();

const currenciesStore = useCurrenciesStore();
const walletAddressesStore = useWalletAddressesStore();
const clientInfoStore = useClientInfoStore();

const { dcaRequest, requestCode } = useDca();
const { t } = useTranslations();

const fields = reactive<ValidatedFormFields>({});

const updateField = (data: ValidatedFieldData): void => {
    fields[data.name] = data;
    dcaRequest[data.name] = data.value;
};

const triggerSubmitValidation = ref<boolean>(false);
provide<Ref<boolean>>(SubmittedSymbol, triggerSubmitValidation);

const submit = (): void => {
    if (Object.values(fields).some((item) => item.valid === false)) {
        triggerSubmitValidation.value = true;
        return;
    }

    if (!clientInfoStore.client.walletVerificationRequired && !needsVerification(walletAddressesStore.currentWalletAddress)) {
        requestDcaCode();
        return;
    }

    if (walletAddressesStore.currentWalletAddressType === 'select' && !!walletAddressesStore.selectedWalletAddress) {
        const isVerifiedWalletAddress = walletAddressesStore.currentWalletAddress.identifier === walletAddressesStore.selectedWalletAddress.identifier;
        if (isVerifiedWalletAddress) {
            requestDcaCode();
            return;
        }
    }

    emit(EmitEvents.VERIFY);
};

async function requestDcaCode(): Promise<void> {
    try {
        await requestCode();
        emit(EmitEvents.COMPLETED);
    } catch (exception) {
        emit(EmitEvents.FAILED);
    }
}

const minimum = computed<number>(() => {
    const currency = currenciesStore.selectedCurrencyPair;
    if (!currency) {
        return;
    }

    return currency.buy.min.amount;
});

const paymentMethodsStore = usePaymentMethodsStore();
const maximum = computed<number>(() => paymentMethodsStore.bankTransferLimit ?? 50000);
</script>
